import { PRODUCT_VIEW_MODE, PRODUCT_VIEW_MODE_LABEL_KEYS } from '../constants/viewModes.js'
import { VIEWER_VARIANT } from '../constants/viewerVariant.js'
import { COMPARTMENT_LIST_BEHAVIOR } from '../constants/compartmentListBehavior.js'

/**
 * PRODUCT_MODE_REGISTRY — declarative configuration for all product viewer modes.
 *
 * Each entry defines:
 * - supportedViewerVariants: which viewer variants (2D/3D) this mode supports
 * - hasController: true if ModeControllerPane should render a controller for this mode
 * - isSupported(capabilities): guard — can this mode run for this product's data?
 * - buildAvailability(capabilities, panelModesConfig): visible/disabled state + disabledReason
 *
 * Adding a new mode = adding one entry here. No switches elsewhere.
 *
 * Controller components are resolved by mode id in ModeControllerPane (not here,
 * to avoid circular imports between registry ↔ React component files).
 */

const _2D_ONLY = [VIEWER_VARIANT._2D]
const _2D_AND_3D = [VIEWER_VARIANT._2D, VIEWER_VARIANT._3D]

export const PRODUCT_MODE_REGISTRY = [
  {
    id: PRODUCT_VIEW_MODE.INFO,
    labelKey: PRODUCT_VIEW_MODE_LABEL_KEYS[PRODUCT_VIEW_MODE.INFO],
    supportedViewerVariants: _2D_AND_3D,
    hasController: true,
    compartmentListBehavior: COMPARTMENT_LIST_BEHAVIOR.SELECT,
    isSupported: (_capabilities) => true,
    buildAvailability: (_capabilities, _config) => ({
      isVisible: true,
      isDisabled: false,
      disabledReason: null,
    }),
  },
  {
    id: PRODUCT_VIEW_MODE.HOTSPOT,
    labelKey: PRODUCT_VIEW_MODE_LABEL_KEYS[PRODUCT_VIEW_MODE.HOTSPOT],
    supportedViewerVariants: _2D_AND_3D,
    hasController: false,
    compartmentListBehavior: COMPARTMENT_LIST_BEHAVIOR.SELECT,
    isSupported: (capabilities) => capabilities.hasViewer,
    buildAvailability: (capabilities, _config) => ({
      // Project-level: commissioned? + Dataset-level: ANY product has hotspots?
      isVisible: capabilities.hasViewer && capabilities.hasHotspotCommissioned && capabilities.datasetHasProductWithHotspots,
      // Product-level: THIS product has hotspot cameras?
      isDisabled: !capabilities.hasHotspotCameras,
      disabledReason: !capabilities.hasHotspotCameras ? 'product.mode_disabled_no_hotspot_cameras' : null,
    }),
  },
  {
    id: PRODUCT_VIEW_MODE.INSOLATION,
    labelKey: PRODUCT_VIEW_MODE_LABEL_KEYS[PRODUCT_VIEW_MODE.INSOLATION],
    supportedViewerVariants: _2D_AND_3D,
    hasController: true,
    compartmentListBehavior: COMPARTMENT_LIST_BEHAVIOR.SELECT,
    isSupported: (capabilities) => capabilities.hasViewer,
    buildAvailability: (capabilities, _config) => ({
      // Project-level: commissioned? + Dataset-level: ANY product has insolation?
      isVisible: capabilities.hasViewer && capabilities.hasInsolationCommissioned && capabilities.datasetHasProductWithInsolation,
      // Product-level: THIS product has insolation data?
      isDisabled: !capabilities.hasInsolationData,
      disabledReason: !capabilities.hasInsolationData ? 'product.mode_disabled_no_insolation_data' : null,
    }),
  },
  {
    id: PRODUCT_VIEW_MODE.ARRANGE,
    labelKey: PRODUCT_VIEW_MODE_LABEL_KEYS[PRODUCT_VIEW_MODE.ARRANGE],
    supportedViewerVariants: _2D_ONLY,
    hasController: false,
    compartmentListBehavior: COMPARTMENT_LIST_BEHAVIOR.ARRANGE,
    isSupported: (capabilities) => capabilities.hasViewer,
    buildAvailability: (capabilities, _config) => ({
      // Project-level: commissioned? + Dataset-level: ANY product has variants?
      isVisible: capabilities.hasViewer && capabilities.hasArrangeCommissioned && capabilities.datasetHasProductWithCompartmentVariants,
      // Product-level: THIS product has compartment variants?
      isDisabled: !capabilities.hasCompartmentVariants,
      disabledReason: !capabilities.hasCompartmentVariants ? 'product.mode_disabled_no_compartment_variants' : null,
    }),
  },
  {
    id: PRODUCT_VIEW_MODE.AXONOMETRIC,
    labelKey: PRODUCT_VIEW_MODE_LABEL_KEYS[PRODUCT_VIEW_MODE.AXONOMETRIC],
    supportedViewerVariants: [],   // axonometric is its own display concept — variant toggle n/a
    hasController: false,
    compartmentListBehavior: COMPARTMENT_LIST_BEHAVIOR.AXONOMETRIC,
    isSupported: (capabilities) => capabilities.hasViewer,
    buildAvailability: (capabilities, _config) => ({
      // Project-level: commissioned? + Dataset-level: ANY product has axonometric camera?
      isVisible: capabilities.hasViewer && capabilities.hasAxonometricCommissioned && capabilities.datasetHasProductWithAxonometric,
      // Product-level: THIS product has axonometric camera?
      isDisabled: !capabilities.hasAxonometricCamera,
      disabledReason: !capabilities.hasAxonometricCamera ? 'product.mode_disabled_no_axonometric_camera' : null,
    }),
  },
]

/**
 * getModeEntry — returns the registry entry for a given mode id.
 * Returns undefined if the mode is not registered.
 *
 * @param {string} modeId
 * @returns {object|undefined}
 */
export function getModeEntry(modeId) {
  return PRODUCT_MODE_REGISTRY.find((entry) => entry.id === modeId)
}

/**
 * getSupportedVariantsForMode — returns supported viewer variants for a given mode id.
 * Fallback: 2D only if mode is unknown.
 *
 * @param {string} modeId
 * @returns {string[]}
 */
export function getSupportedVariantsForMode(modeId) {
  return getModeEntry(modeId)?.supportedViewerVariants ?? [VIEWER_VARIANT._2D]
}

/**
 * getModeHasController — returns whether ModeControllerPane should render
 * a controller UI for the given mode.
 *
 * @param {string} modeId
 * @returns {boolean}
 */
export function getModeHasController(modeId) {
  return getModeEntry(modeId)?.hasController ?? false
}

/**
 * getCompartmentListBehavior — returns the compartment list rendering behavior
 * for a given mode: COMPARTMENT_LIST_BEHAVIOR.SELECT | .ARRANGE | .AXONOMETRIC.
 * Returns null if the mode is not registered.
 *
 * @param {string} modeId
 * @returns {string|null}
 */
export function getCompartmentListBehavior(modeId) {
  return getModeEntry(modeId)?.compartmentListBehavior ?? null
}
